import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Card,
    Tabs,
    Table,
    Input,
    Select,
    Button,
    Space,
    Row,
    Col,
    Alert,
    Dropdown,
    Typography,
    message,
    Pagination,
    Modal,
    InputNumber,
    Tooltip,
    DatePicker,
    Checkbox
} from 'antd';
import {
    SearchOutlined,
    DownloadOutlined,
    PrinterOutlined,
    FileExcelOutlined,
    BarChartOutlined,
    MoreOutlined,
    PlusOutlined,
    EditOutlined,
    FilterOutlined,
    EyeOutlined
} from '@ant-design/icons';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { API_BASE_URL } from '../../utils/config';
import { getNumericFilters, getStringFilters, getDateFilters, formatCellValue, applyAdvancedFilter, redirectToPivotWithData, exportToExcel, printTable } from '../../utils/tableFilters';
import { trIncludes } from '../../utils/trText';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import FormikForm from '../form/FormikForm';
import './KurulumHareketleriPage.css';
import { useNavigate } from 'react-router-dom';
import Chatbot from '../giris/Chatbot.jsx';
import DepoKabinGirisiTab from '../depo/DepoKabinGirisiTab';

dayjs.extend(customParseFormat);
dayjs.extend(isBetween);

const { Title, Text } = Typography;
const { Option } = Select;

const DepoPage = () => {
    const navigate = useNavigate();
    const { darkMode } = useTheme();
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('depo-hareketleri');
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        search: '',
        page: 1,
        pageSize: 10,
    });
    const [pagination, setPagination] = useState({
        current: 1,
        total: 0,
        pageSize: 10
    });
    const [formModalOpen, setFormModalOpen] = useState(false);
    const [currentFormId, setCurrentFormId] = useState('');
    const [modalTitle, setModalTitle] = useState('');
    const [formTableName, setFormTableName] = useState('');
    const [formUniqueField, setFormUniqueField] = useState(null);
    const [currentUniqueValue, setCurrentUniqueValue] = useState(null);
    const [formExtraData, setFormExtraData] = useState({});  
    const [filteredInfo, setFilteredInfo] = useState({});
    const [sortedInfo, setSortedInfo] = useState({});
    const [columns, setColumns] = useState([]);
    const [showData, setShowData] = useState([]);
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [columnSelectDropdownOpen, setColumnSelectDropdownOpen] = useState(false);

    useEffect(() => {
        if (darkMode) {
            document.body.setAttribute('data-theme', 'dark');
        } else {
            document.body.removeAttribute('data-theme');
        }
    }, [darkMode]);

    const ActionButtons = ({ record, actionButtons, darkMode }) => {
        if (!actionButtons || actionButtons.length === 0) {
            return null;
        }

        if (actionButtons.length > 2) {
            const menuItems = actionButtons.map(button => {
                return {
                    key: button.key,
                    label: button.label,
                    onClick: () => button.onClick(record)
                };
            });

            return (
                <Dropdown
                    menu={{ items: menuItems }}
                    trigger={['click']}
                    placement="bottomRight"
                >
                    <Button
                        type="text"
                        icon={<MoreOutlined />}
                        size="small"
                        className={darkMode ? 'dark-button' : ''}
                        style={{
                            color: darkMode ? 'var(--dark-text)' : undefined
                        }}
                    />
                </Dropdown>
            );
        }

        return (
            <Space size="small">
                {actionButtons.map(button => {
                    return (
                        <Button
                            key={button.key}
                            type={button.type}
                            size="small"
                            onClick={() => button.onClick(record)}
                            className={darkMode ? 'dark-button' : ''}
                            style={{
                                backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                color: darkMode ? 'var(--dark-text)' : undefined
                            }}
                        >
                            {button.label}
                        </Button>
                    );
                })}
            </Space>
        );
    };

    const depoTabs = [
        {
            key: 'depo-hareketleri',
            label: 'Depo Hareketleri',
            sqlKey: 'depo-hareketleri',
            formManagerId: 195, 
            actionButtons: [
                {
                    key: 'edit',
                    label: 'Düzenle',
                    type: 'default',
                    icon: 'EditOutlined',
                    onClick: (record) => {                                                
                        const id = record.key_kabin_islem;
                        const platform = record["Platform No"];
                        if (id) {
                            const extraData = {
                                kabin: platform,
                                kullanici_no: user?.kullanici_no || null,
                                nereden_name: record.Nereden,
                            };
                            // 'Düzenle' MEVCUT taşıma kaydını günceller. Daha önce
                            // 'Insert' geçiliyordu ve her düzeltme tarihçeye
                            // mükerrer kayıt üretiyordu.
                            handleOpenForm(75, 'Platform Taşıma Formu', 'kabin_tasima', 'id', id, extraData, 'Update');
                        } else {
                            message.error('ID bulunamadı');
                        }
                    }
                }
            ]
        },
        {
            key: 'kabin-depo',
            label: 'Depodaki Kabinler',
            sqlKey: 'kabin-depo',
            formManagerId: 100, 
            actionButtons: [
                {
                    key: 'view',
                    label: 'Detay',
                    type: 'default',
                    icon: 'EyeOutlined',
                    onClick: (record) => {
                        const id = record['Platform No'];
                        navigate(`/platform_detay/${id}`);
                    }
                },
                {
                    key: 'tasi',
                    label: 'Taşı',
                    type: 'default',
                    icon: 'TransferOutlined',
                    onClick: (record) => {               
                        const id = record['Platform No'];
                        const nereden = record['Depo'];
                        if (id) {
                            const extraData = {
                                kabin: id,
                                nereden_name: nereden,
                                kullanici_no: user?.kullanici_no || null
                            };
                            // YENİ taşıma kaydı: kabin_tasima'ya platform id'si
                            // unique_value olarak geçilirse (a) backend function'ı
                            // Update'e çevirip BAŞKA bir taşımanın üzerine yazıyor,
                            // (b) form o kaydın verisiyle ön-doluyordu.
                            handleOpenForm(75, 'Platform Taşıma Formu', 'kabin_tasima', null, null, extraData, 'Insert');
                        } else {
                            message.error('ID bulunamadı');
                        }
                    }
                },
                {
                    key: 'edit',
                    label: 'Düzenle',
                    type: 'default',
                    icon: 'EditOutlined',
                    onClick: (record) => {
                        const platformId = record['Platform No'];
                        
                        if (platformId) {
                            handleOpenForm(
                                91,                    
                                'Platform Düzenle', 
                                'platformlar',        
                                'id',                  
                                platformId,            
                                {},                   
                                'Update'              
                            );
                        } else {
                            message.error('Platform No bulunamadı');
                        }
                    }
                }
            ]
        },
        {
            key: 'kabin-girisi',
            label: 'Kabin Girişi',
            children: <DepoKabinGirisiTab />
        }
    ];

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const activeTabData = depoTabs.find(tab => tab.key === activeTab);
            if (!activeTabData) {
                console.warn('Active tab data not found for key:', activeTab);
                return;
            }
            if (!activeTabData.sqlKey) {
                // Özel içerikli tab (ör. Kabin Girişi) — genel liste sorgusu çalıştırma
                setData([]);
                setShowData([]);
                setColumns([]);
                return;
            }
            let url, response;

            url = `${API_BASE_URL}/list/execute/${activeTabData.sqlKey}`;
            response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`API Error - Status: ${response.status}, Response:`, errorText);
                throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
            }

            const allData = await response.json();

            if (!Array.isArray(allData)) {
                console.warn('API response is not an array:', allData);
                setError('Geçersiz veri formatı - API array döndürmüyor');
                return;
            }

            let filteredData = [...allData];

            if (filters.search && filters.search.trim() !== '') {
                const searchTerm = filters.search.trim();
                filteredData = filteredData.filter(record => {
                    return Object.values(record).some(value => {
                        if (value === null || value === undefined) return false;
                        return trIncludes(String(value), searchTerm);
                    });
                });
            }

            if (filteredData && filteredData.length > 0) {
                const sample = filteredData[0];
                const dynamicCols = Object.keys(sample)
                    .filter((key) => 
                        !key.toLowerCase().includes('fatura') &&
                        !key.toLowerCase().includes('türü') &&
                        !key.toLowerCase().includes('key_depo') &&
                        !key.toLowerCase().includes('key_kabin_islem')
                    )
                    .map((key) => {
                        const firstValue = sample[key];
                        const isDateColumn = key.toLowerCase().includes('tarih') || key.toLowerCase().includes('tar');
                        const isNumber = typeof firstValue === "number" && !isNaN(firstValue);
                        const uniqueValues = [...new Set(filteredData.map((item) => item[key]))].filter(
                            (val) => val !== undefined && val !== null && val !== ""
                        );

                        let filters = [];
                        if (isNumber) {
                            filters = getNumericFilters();
                        } else if (isDateColumn) {
                            filters = getDateFilters();
                        } else {
                            filters = getStringFilters();
                        }

                        return {
                            key: key,
                            title: key,
                            dataIndex: key,
                            isDateColumn,
                            isNumber,
                            isString: !isNumber && !isDateColumn,
                            uniqueValues,
                            sortable: true,
                            filters, 
                        };
                    });
                setColumns(dynamicCols);
            }
            setData(filteredData || []);
            setShowData(filteredData || []);
            setPagination(prev => ({
                ...prev,
                total: filteredData?.length || 0
            }));

        } catch (err) {
            console.error('Depo data load error:', err);
            setError('Veri yüklenirken hata oluştu: ' + err.message);
        } finally {
            setLoading(false);
        }
    }, [activeTab, filters.search]);

    const handlePivotRedirect = useCallback(() => {
        const activeTabData = depoTabs.find(tab => tab.key === activeTab);
        if (!activeTabData?.sqlKey) return;
        redirectToPivotWithData({
            data,
            filters,
            tabLabel: activeTabData.label || 'Depo',
            sqlKey: activeTabData.sqlKey,
            navigate,
            message,
            setLoading,
            apiBaseUrl: API_BASE_URL,
        });
    }, [activeTab, data, filters, navigate]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
        setPagination(prev => ({
            ...prev,
            current: 1
        }));
    };

    const handleTabChange = (key) => {
        setActiveTab(key);
        const newTabData = depoTabs.find(tab => tab.key === key);
        setFilters({
            search: '',
            page: 1,
            pageSize: 25,
        });
        setPagination({
            current: 1,
            total: 0,
            pageSize: 10
        });
    };

    const handlePageChange = (page, pageSize) => {
        setPagination(prev => ({
            ...prev,
            current: page,
            pageSize: pageSize || prev.pageSize
        }));
    };

    const handleOpenForm = (formId, title = 'Form', tableName = '', uniqueField = '', uniqueValue = null, extraData = {}, overrideFunction = null) => {
        setModalTitle(title);
        setCurrentFormId(formId);
        setFormTableName(tableName);
        setFormUniqueField(uniqueField);
        setCurrentUniqueValue(uniqueValue);
        setFormExtraData({
            ...extraData,
            ...(overrideFunction && { _override_function: overrideFunction })  
        });  
        setFormModalOpen(true);
    };

    const handleFormSuccess = () => {
        setFormModalOpen(false);
        setCurrentFormId(null);
        setFormTableName('');
        setFormUniqueField('');
        setFormExtraData({});
        loadData();
    };

    const closeFormModal = () => {
        setFormModalOpen(false);
        setFormExtraData({});
    };

    const handleAdvancedFilterChange = (columnKey, valueType, value) => {
        setFilteredInfo((prev) => ({
            ...prev,
            [columnKey]: {
                ...prev[columnKey],
                [valueType]: value,
            },
        }));
    };

    const clearColumnFilter = (columnKey) => {
        setFilteredInfo((prev) => {
            const newFilteredInfo = { ...prev };
            delete newFilteredInfo[columnKey];
            return newFilteredInfo;
        });
    };

    const clearAllFilters = useCallback(() => {
        setFilteredInfo({});
        setSortedInfo({});
        setFilters({
            search: '',
            page: 1,
            pageSize: 50
        });
    }, []);

    const handleSort = (columnKey) => {
        const column = columns.find((col) => col.key === columnKey);
        if (!column) return;

        let newOrder = "asc";
        if (sortedInfo.columnKey === columnKey) {
            if (sortedInfo.order === "asc") {
                newOrder = "desc";
            } else if (sortedInfo.order === "desc") {
                newOrder = null;
            }
        }

        setSortedInfo({
            columnKey: newOrder ? columnKey : null,
            order: newOrder,
        });
    };

    const removeColumn = (columnKey) => {
        setSelectedColumns((prev) => {
            const newSelected = prev.filter((key) => key !== columnKey);
            saveSelectedColumnsToStorage(newSelected);
            return newSelected;
        });
    };

    const filteredAndSortedData = useMemo(() => {
        let result = data.filter((item) => {
            const matchesSearch = Object.values(item).some((val) =>
                trIncludes(String(val), filters.search)
            );

            const matchesFilters = Object.entries(filteredInfo).every(([key, filter]) => {
                const recordValue = item[key];
                const col = columns.find((c) => c.key === key);
                return applyAdvancedFilter(recordValue, filter, col);
            });

            return matchesSearch && matchesFilters;
        });

        if (sortedInfo.columnKey && sortedInfo.order) {
            const column = columns.find((col) => col.key === sortedInfo.columnKey);
            if (column) {
                result.sort((a, b) => {
                    const valA = a[sortedInfo.columnKey];
                    const valB = b[sortedInfo.columnKey];

                    if (column.isDateColumn) {
                        let dateA = null, dateB = null;
                        
                        if (valA) {
                            const dateValueA = String(valA).trim();
                            if (dateValueA === '0000-00-00' || dateValueA === '0000-00-00 00:00:00' || dateValueA === '1900-01-01' || dateValueA === '' || dateValueA === 'null' || dateValueA === 'undefined') {
                                dateA = null; 
                            } else if (dateValueA.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
                                dateA = dayjs(dateValueA, 'DD.MM.YYYY');
                            } else if (dateValueA.match(/^\d{4}-\d{2}-\d{2}$/)) {
                                dateA = dayjs(dateValueA, 'YYYY-MM-DD');
                            } else if (dateValueA.match(/^\d{4}\.\d{2}\.\d{2}$/)) {
                                dateA = dayjs(dateValueA, 'YYYY.MM.DD');
                            } else if (dateValueA.match(/^\d{2}-\d{2}-\d{4}$/)) {
                                dateA = dayjs(dateValueA, 'DD-MM-YYYY');
                            } else if (dateValueA.match(/^\d{4}\/\d{2}\/\d{2}$/)) {
                                dateA = dayjs(dateValueA, 'YYYY/MM/DD');
                            } else if (dateValueA.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
                                dateA = dayjs(dateValueA, 'DD/MM/YYYY');
                            } else {
                                dateA = dayjs(valA);
                            }
                        }
                        
                        if (valB) {
                            const dateValueB = String(valB).trim();
                            if (dateValueB === '0000-00-00' || dateValueB === '0000-00-00 00:00:00' || dateValueB === '1900-01-01' || dateValueB === '' || dateValueB === 'null' || dateValueB === 'undefined') {
                                dateB = null; 
                            } else if (dateValueB.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
                                dateB = dayjs(dateValueB, 'DD.MM.YYYY');
                            } else if (dateValueB.match(/^\d{4}-\d{2}-\d{2}$/)) {
                                dateB = dayjs(dateValueB, 'YYYY-MM-DD');
                            } else if (dateValueB.match(/^\d{4}\.\d{2}\.\d{2}$/)) {
                                dateB = dayjs(dateValueB, 'YYYY.MM.DD');
                            } else if (dateValueB.match(/^\d{2}-\d{2}-\d{4}$/)) {
                                dateB = dayjs(dateValueB, 'DD-MM-YYYY');
                            } else if (dateValueB.match(/^\d{4}\/\d{2}\/\d{2}$/)) {
                                dateB = dayjs(dateValueB, 'YYYY/MM/DD');
                            } else if (dateValueB.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
                                dateB = dayjs(dateValueB, 'DD/MM/YYYY');
                            } else {
                                dateB = dayjs(valB);
                            }
                        }
                        
                        if (!dateA && !dateB) return 0;
                        if (!dateA || !dateA.isValid()) return sortedInfo.order === "asc" ? -1 : 1;
                        if (!dateB || !dateB.isValid()) return sortedInfo.order === "asc" ? 1 : -1;
                        const comparison = dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
                        return sortedInfo.order === "asc" ? comparison : -comparison;
                    } else if (column.isNumber) {
                        const numA = Number(valA) || 0;
                        const numB = Number(valB) || 0;
                        return sortedInfo.order === "asc" ? numA - numB : numB - numA;
                    } else {
                        const strA = String(valA || "").toLowerCase();
                        const strB = String(valB || "").toLowerCase();
                        const comparison = strA.localeCompare(strB, 'tr');
                        return sortedInfo.order === "asc" ? comparison : -comparison;
                    }
                });
            }
        }

        return result;
    }, [data, filters.search, filteredInfo, sortedInfo, columns]);

    const getFilterDropdown = (column) => {
        const currentFilter = filteredInfo[column.key] || {};
        const condition = currentFilter.condition || "choose";

        return (
            <div style={{ padding: 16, minWidth: 'min(300px, 85vw)' }}>
                <Space direction="vertical" style={{ width: '100%', backgroundColor: darkMode ? 'var(--dark-bg)' : 'white', border: `1px solid ${darkMode ? 'var(--dark-border)' : '#d9d9d9'}`, padding: 12}}>
                    <div>
                        <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                            Filtre Şartı
                        </Text>
                        <Select
                            size="small"
                            value={condition}
                            onChange={(value) => handleAdvancedFilterChange(column.key, "condition", value)}
                            style={{ width: '100%' }}
                            className={darkMode ? 'dark-select' : ''}
                        >
                            {column.filters.map((filter) => (
                                <Option key={filter.value} value={filter.value}>
                                    {filter.text}
                                </Option>
                            ))}
                        </Select>
                    </div>

                    {/* String değer seçimi */}
                    {column.isString && condition === "select" && (
                        <div>
                            <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                                Değer Seç
                            </Text>
                            <Select
                                size="small"
                                value={currentFilter.value || ""}
                                onChange={(value) => handleAdvancedFilterChange(column.key, "value", value)}
                                style={{ width: '100%' }}
                                placeholder="Seçiniz"
                                className={darkMode ? 'dark-select' : ''}
                            >
                                <Option value="">Seçiniz</Option>
                                {column.uniqueValues.map((val) => (
                                    <Option key={val} value={val}>
                                        {val}
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    )}

                    {/* Sayısal filtreler için input alanları */}
                    {column.isNumber && condition !== "choose" && condition !== "between" && (
                        <div>
                            <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                                Değer
                            </Text>
                            <InputNumber
                                size="small"
                                placeholder="Değer giriniz"
                                value={currentFilter.value || ""}
                                onChange={(value) => handleAdvancedFilterChange(column.key, "value", value)}
                                style={{ width: '100%' }}
                                className={darkMode ? 'dark-input' : ''}
                            />
                        </div>
                    )}

                    {/* Sayısal arasında filtresi */}
                    {column.isNumber && condition === "between" && (
                        <>
                            <div>
                                <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                                    Min Değer
                                </Text>
                                <InputNumber
                                    size="small"
                                    placeholder="Min"
                                    value={currentFilter.min || ""}
                                    onChange={(value) => handleAdvancedFilterChange(column.key, "min", value)}
                                    style={{ width: '100%' }}
                                    className={darkMode ? 'dark-input' : ''}
                                />
                            </div>
                            <div>
                                <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                                    Max Değer
                                </Text>
                                <InputNumber
                                    size="small"
                                    placeholder="Max"
                                    value={currentFilter.max || ""}
                                    onChange={(value) => handleAdvancedFilterChange(column.key, "max", value)}
                                    style={{ width: '100%' }}
                                    className={darkMode ? 'dark-input' : ''}
                                />
                            </div>
                        </>
                    )}

                    {/* String filtreler için input alanları */}
                    {column.isString && !["choose", "empty", "notEmpty", "select"].includes(condition) && (
                        <div>
                            <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                                Değer
                            </Text>
                            <Input
                                size="small"
                                placeholder="Değer giriniz"
                                value={currentFilter.value || ""}
                                onChange={(e) => handleAdvancedFilterChange(column.key, "value", e.target.value)}
                                style={{ width: '100%' }}
                                className={darkMode ? 'dark-input' : ''}
                            />
                        </div>
                    )}

                    {/* Tarih filtreler için input alanları */}
                    {column.isDateColumn && ["equals", "before", "after"].includes(condition) && (
                        <div>
                            <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                                Tarih
                            </Text>
                            <DatePicker
                                size="small"
                                value={currentFilter.value ? dayjs(currentFilter.value, 'DD.MM.YYYY') : null}
                                onChange={(date) => handleAdvancedFilterChange(column.key, "value", date ? date.format('DD.MM.YYYY') : null)}
                                style={{ width: '100%' }}
                                format="DD.MM.YYYY"
                                className={darkMode ? 'dark-date-picker' : ''}
                            />
                        </div>
                    )}

                    {/* Tarih arasında filtresi */}
                    {column.isDateColumn && condition === "between" && (
                        <>
                            <div>
                                <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                                    Başlangıç Tarihi
                                </Text>
                                <DatePicker
                                    size="small"
                                    value={currentFilter.startDate ? dayjs(currentFilter.startDate, 'DD.MM.YYYY') : null}
                                    onChange={(date) => handleAdvancedFilterChange(column.key, "startDate", date ? date.format('DD.MM.YYYY') : null)}
                                    style={{ width: '100%' }}
                                    format="DD.MM.YYYY"
                                    className={darkMode ? 'dark-date-picker' : ''}
                                />
                            </div>
                            <div>
                                <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                                    Bitiş Tarihi
                                </Text>
                                <DatePicker
                                    size="small"
                                    value={currentFilter.endDate ? dayjs(currentFilter.endDate, 'DD.MM.YYYY') : null}
                                    onChange={(date) => handleAdvancedFilterChange(column.key, "endDate", date ? date.format('DD.MM.YYYY') : null)}
                                    style={{ width: '100%' }}
                                    format="DD.MM.YYYY"
                                    className={darkMode ? 'dark-date-picker' : ''}
                                />
                            </div>
                        </>
                    )}

                    <Button
                        size="small"
                        onClick={() => clearColumnFilter(column.key)}
                        style={{ marginTop: 8, width: '100%' }}
                        className={darkMode ? 'dark-button' : ''}
                    >
                        Filtreyi Sıfırla
                    </Button>
                </Space>
            </div>
        );
    };

    useEffect(() => {
        const activeTabData = depoTabs.find(tab => tab.key === activeTab);
        if (activeTabData) {
            loadData();
        }
    }, [loadData, activeTab]);

    const saveSelectedColumnsToStorage = useCallback((columnKeys) => {
        try {
            const storageKey = `depo_selected_columns_${activeTab}`;
            localStorage.setItem(storageKey, JSON.stringify(columnKeys));
        } catch (error) {
            console.error('Error saving selected columns to storage:', error);
        }
    }, [activeTab]);

    useEffect(() => {
        if (columns.length > 0) {
            try {
                const storageKey = `depo_selected_columns_${activeTab}`;
                const saved = localStorage.getItem(storageKey);
                if (saved) {
                    const parsed = JSON.parse(saved);
                    const validColumns = parsed.filter(key => columns.some(col => col.key === key));
                    if (validColumns.length > 0) {
                        setSelectedColumns(validColumns);
                    } else {
                        const allColumnKeys = columns.map(col => col.key);
                        setSelectedColumns(allColumnKeys);
                        saveSelectedColumnsToStorage(allColumnKeys);
                    }
                } else {
                    const allColumnKeys = columns.map(col => col.key);
                    setSelectedColumns(allColumnKeys);
                    saveSelectedColumnsToStorage(allColumnKeys);
                }
            } catch (error) {
                console.error('Error loading selected columns from storage:', error);
                const allColumnKeys = columns.map(col => col.key);
                setSelectedColumns(allColumnKeys);
            }
        }
    }, [columns, activeTab, saveSelectedColumnsToStorage]);

    const handleColumnSelectionChange = (checkedValues) => {
        setSelectedColumns(checkedValues);
        saveSelectedColumnsToStorage(checkedValues);
    };

    const handleSelectAllColumns = (checked) => {
        if (checked) {
            const allColumnKeys = columns.map(col => col.key);
            setSelectedColumns(allColumnKeys);
            saveSelectedColumnsToStorage(allColumnKeys);
        } else {
            setSelectedColumns([]);
            saveSelectedColumnsToStorage([]);
        }
    };

    const visibleColumns = useMemo(() => {
        if (selectedColumns.length === 0) {
            return columns;
        }
        return columns.filter(col => selectedColumns.includes(col.key));
    }, [columns, selectedColumns]);

    return (
        <div className={`kurulum-page ${darkMode ? 'dark-theme' : ''}`}>
            {/* Filtreler */}
            <Card
                className={`kurulum-filters-card ${darkMode ? 'dark-card' : ''}`}
                style={{
                    marginTop: 16,
                    backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined,
                    borderColor: darkMode ? 'var(--dark-border)' : undefined
                }}
            >
                <Row gutter={[24, 24]} align="middle">
                    {/* Depo Dropdown */}
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-item">
                            <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                Depo Verileri
                                <EditOutlined onClick={() => {
                                    const activeTabData = depoTabs.find(tab => tab.key === activeTab);
                                    const formManagerId = activeTabData?.formManagerId;
                                    handleOpenForm(275, 'Liste Formu', 'sqls', 'id', formManagerId, {});
                                }} />
                            </Text>
                            <Select
                                placeholder="Depo Verisi Seçin"
                                value={activeTab}
                                onChange={(value) => handleTabChange(value)}
                                style={{ width: '100%' }}
                                className={darkMode ? 'dark-select' : ''}
                            >
                                {depoTabs.map(tab => (
                                    <Option key={tab.key} value={tab.key}>
                                        {tab.label}
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    </Col>
                    {/* Arama */}
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-item">
                            <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                Arama
                            </Text>
                            <Input
                                placeholder="Arama yapın..."
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                prefix={<SearchOutlined style={{ color: darkMode ? 'var(--dark-text-muted)' : undefined }} />}
                                allowClear
                                style={{
                                    backgroundColor: darkMode ? 'var(--dark-bg)' : undefined,
                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                    color: darkMode ? 'var(--dark-text)' : undefined
                                }}
                                className={darkMode ? 'dark-input' : ''}
                            />
                        </div>
                    </Col>

                    {/* Aksiyon Butonları */}
                    <Col xs={24} sm={24} md={8} lg={12}>
                        <div className="filter-actions" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Space wrap>
                                <Button
                                    onClick={clearAllFilters}
                                    className={darkMode ? 'dark-button' : ''}
                                    style={{
                                        backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                        borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                        color: darkMode ? 'var(--dark-text)' : undefined
                                    }}
                                >
                                    Temizle
                                </Button>
                                {/* Ekle butonu */}
                                {activeTab === 'kabin-depo' && (
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        onClick={() => {
                                            handleOpenForm(309, 'Platform Ekle', 'platformlar', '', null, {});
                                        }}
                                        className={darkMode ? 'dark-button' : ''}
                                        style={{
                                            backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                            borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                            color: darkMode ? 'var(--dark-text)' : undefined
                                        }}
                                    >
                                        Ekle
                                    </Button>
                                )}
                                <Dropdown
                                    menu={{
                                        items: [
                                            {
                                                key: 'excel',
                                                icon: <FileExcelOutlined />,
                                                label: 'Excel (.xlsx)',
                                                onClick: () => exportToExcel(filteredAndSortedData)
                                            },
                                            {
                                                key: 'print',
                                                icon: <PrinterOutlined />,
                                                label: 'Yazdır',
                                                onClick: () => printTable(filteredAndSortedData, { title: depoTabs.find(t => t.key === activeTab)?.label || 'Depo Verileri', message })
                                            }
                                        ]
                                    }}
                                    trigger={['click']}
                                >
                                    <Button
                                        icon={<DownloadOutlined />}
                                        className={darkMode ? 'dark-button' : ''}
                                        style={{
                                            backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                            borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                            color: darkMode ? 'var(--dark-text)' : undefined
                                        }}
                                    >
                                        Dışa Aktar
                                    </Button>
                                </Dropdown>
                                <Button
                                    icon={<BarChartOutlined />}
                                    onClick={handlePivotRedirect}
                                    disabled={loading || data.length === 0}
                                    className={darkMode ? 'dark-button' : ''}
                                    style={{
                                        backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                        borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                        color: darkMode ? 'var(--dark-text)' : undefined
                                    }}
                                >
                                    Pivot Tabloya Git
                                </Button>
                                <Dropdown
                                    open={columnSelectDropdownOpen}
                                    onOpenChange={setColumnSelectDropdownOpen}
                                    trigger={['click']}
                                    dropdownRender={() => (
                                        <div style={{
                                            padding: '2px',
                                            minWidth: '250px',
                                            maxHeight: '400px',
                                            overflowY: 'auto',
                                            backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : '#fff',
                                            border: `1px solid ${darkMode ? 'var(--dark-border)' : '#d9d9d9'}`,
                                            borderRadius: '6px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                        }}>
                                            <div style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: `1px solid ${darkMode ? 'var(--dark-border)' : '#d9d9d9'}` }}>
                                                <Checkbox
                                                    checked={selectedColumns.length === columns.length && columns.length > 0}
                                                    indeterminate={selectedColumns.length > 0 && selectedColumns.length < columns.length}
                                                    onChange={(e) => handleSelectAllColumns(e.target.checked)}
                                                    style={{ color: darkMode ? 'var(--dark-text)' : undefined }}
                                                >
                                                    <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                                        Tümünü Seç
                                                    </Text>
                                                </Checkbox>
                                            </div>
                                            <Checkbox.Group
                                                value={selectedColumns}
                                                onChange={handleColumnSelectionChange}
                                                style={{ width: '100%' }}
                                            >
                                                <Space direction="vertical" style={{ width: '100%' }}>
                                                    {columns.map(column => (
                                                        <Checkbox
                                                            key={column.key}
                                                            value={column.key}
                                                            style={{
                                                                width: '100%',
                                                                color: darkMode ? 'var(--dark-text)' : undefined
                                                            }}
                                                        >
                                                            {column.title}
                                                        </Checkbox>
                                                    ))}
                                                </Space>
                                            </Checkbox.Group>
                                        </div>
                                    )}
                                >
                                    <Button
                                        icon={<EyeOutlined />}
                                        className={darkMode ? 'dark-button' : ''}
                                        style={{
                                            backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                            borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                            color: darkMode ? 'var(--dark-text)' : undefined
                                        }}
                                    >
                                        Sütun Seç
                                    </Button>
                                </Dropdown>
                                {/* Chatbot */}
                                <Chatbot />
                            </Space>
                        </div>
                    </Col>
                </Row>
            </Card>

            {/* Özel içerikli tab (ör. Kabin Girişi) */}
            {depoTabs.find(tab => tab.key === activeTab)?.children ? (
                <Card
                    className={`kurulum-content-card ${darkMode ? 'dark-card' : ''}`}
                    style={{
                        marginTop: 16,
                        backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined,
                        borderColor: darkMode ? 'var(--dark-border)' : undefined
                    }}
                >
                    {depoTabs.find(tab => tab.key === activeTab).children}
                </Card>
            ) : (
            <Card
                className={`kurulum-content-card ${darkMode ? 'dark-card' : ''}`}
                style={{
                    marginTop: 16,
                    backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined,
                    borderColor: darkMode ? 'var(--dark-border)' : undefined
                }}
            >
                <div>
                    {/* Tablo */}
                    {error && (
                        <Alert
                            message="Hata"
                            description={error}
                            type="error"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    <Table
                        dataSource={filteredAndSortedData}
                        loading={loading}
                        rowKey={(record) => record['Platform No']}
                        scroll={{ x: 'max-content' }}
                        scroll={{ x: 'max-content' }}
                        className={darkMode ? 'dark-table' : ''}
                        pagination={{
                            current: pagination.current,
                            total: filteredAndSortedData.length,
                            pageSize: pagination.pageSize,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) =>
                                `Toplam ${total} kayıt (${range[0]}-${range[1]})`,
                            onChange: handlePageChange,
                            onShowSizeChange: handlePageChange,
                            pageSizeOptions: ['10', '25', '50', '100']
                        }}
                        size="small"
                        style={{
                            width: '100%',
                            backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined
                        }}
                        columns={[
                            ...visibleColumns.map((column) => {
                                const isActive = filteredInfo[column.key] && filteredInfo[column.key].condition && filteredInfo[column.key].condition !== "choose";
                                
                                return {
                                    ...column,
                                    filters: undefined,
                                    filterDropdown: false,
                                    title: (
                                        <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer' }}>{column.title}</span>
                                            <Dropdown
                                                dropdownRender={() => getFilterDropdown(column)}
                                                trigger={['click']}
                                                placement="bottomRight"
                                            >
                                                <Button 
                                                    type="text" 
                                                    size="small" 
                                                    style={{ 
                                                        marginLeft: 2,
                                                        padding: '0 2px',
                                                        minWidth: '16px',
                                                        height: '16px',
                                                        color: isActive ? (darkMode ? 'var(--dark-text)' : '#1890ff') : '#ccc'
                                                    }}
                                                >
                                                    <FilterOutlined style={{ fontSize: 10 }} />
                                                </Button>
                                            </Dropdown>
                                        </div>
                                    ),
                                    onHeaderCell: () => ({
                                        onContextMenu: (e) => {
                                            e.preventDefault();
                                            removeColumn(column.key);
                                        },
                                        onClick: () => {
                                            if (column.sortable) {
                                                handleSort(column.key);
                                            }
                                        }
                                    }),
                                    dataIndex: column.key,
                                    key: column.key,
                                    align: 'center',
                                    width: 50,
                                    sorter: column.sortable ? (a, b) => {
                                        const valA = a[column.key];
                                        const valB = b[column.key];
                                        if (column.isDateColumn) {
                                            let dateA = null, dateB = null;
                                            
                                            if (valA) {
                                                const dateValueA = String(valA).trim();
                                                if (dateValueA === '0000-00-00' || dateValueA === '0000-00-00 00:00:00' || dateValueA === '1900-01-01' || dateValueA === '' || dateValueA === 'null' || dateValueA === 'undefined') {
                                                    dateA = null; 
                                                } else if (dateValueA.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
                                                    dateA = dayjs(dateValueA, 'DD.MM.YYYY');
                                                } else if (dateValueA.match(/^\d{4}-\d{2}-\d{2}$/)) {
                                                    dateA = dayjs(dateValueA, 'YYYY-MM-DD');
                                                } else if (dateValueA.match(/^\d{4}\.\d{2}\.\d{2}$/)) {
                                                    dateA = dayjs(dateValueA, 'YYYY.MM.DD');
                                                } else if (dateValueA.match(/^\d{2}-\d{2}-\d{4}$/)) {
                                                    dateA = dayjs(dateValueA, 'DD-MM-YYYY');
                                                } else if (dateValueA.match(/^\d{4}\/\d{2}\/\d{2}$/)) {
                                                    dateA = dayjs(dateValueA, 'YYYY/MM/DD');
                                                } else if (dateValueA.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
                                                    dateA = dayjs(dateValueA, 'DD/MM/YYYY');
                                                } else {
                                                    dateA = dayjs(valA);
                                                }
                                            }
                                            
                                            if (valB) {
                                                const dateValueB = String(valB).trim();
                                                if (dateValueB === '0000-00-00' || dateValueB === '0000-00-00 00:00:00' || dateValueB === '1900-01-01' || dateValueB === '' || dateValueB === 'null' || dateValueB === 'undefined') {
                                                    dateB = null; 
                                                } else if (dateValueB.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
                                                    dateB = dayjs(dateValueB, 'DD.MM.YYYY');
                                                } else if (dateValueB.match(/^\d{4}-\d{2}-\d{2}$/)) {
                                                    dateB = dayjs(dateValueB, 'YYYY-MM-DD');
                                                } else if (dateValueB.match(/^\d{4}\.\d{2}\.\d{2}$/)) {
                                                    dateB = dayjs(dateValueB, 'YYYY.MM.DD');
                                                } else if (dateValueB.match(/^\d{2}-\d{2}-\d{4}$/)) {
                                                    dateB = dayjs(dateValueB, 'DD-MM-YYYY');
                                                } else if (dateValueB.match(/^\d{4}\/\d{2}\/\d{2}$/)) {
                                                    dateB = dayjs(dateValueB, 'YYYY/MM/DD');
                                                } else if (dateValueB.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
                                                    dateB = dayjs(dateValueB, 'DD/MM/YYYY');
                                                } else {
                                                    dateB = dayjs(valB);
                                                }
                                            }
                                            
                                            if (!dateA && !dateB) return 0;
                                            if (!dateA || !dateA.isValid()) return -1;
                                            if (!dateB || !dateB.isValid()) return 1;
                                            return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
                                        } else if (column.isNumber) {
                                            const numA = Number(valA) || 0;
                                            const numB = Number(valB) || 0;
                                            return numA - numB;
                                        } else {
                                            const strA = String(valA || "").toLowerCase();
                                            const strB = String(valB || "").toLowerCase();
                                            return strA.localeCompare(strB);
                                        }
                                    } : false,
                                    showSorterTooltip: false,
                                    render: (text, record) => (
                                        <div 
                                            style={{ 
                                                whiteSpace: 'normal',
                                                wordWrap: 'break-word',
                                                wordBreak: 'break-word',
                                                overflow: 'visible',
                                                width: '100%',
                                                maxWidth: '100%',
                                                display: 'block'
                                            }}
                                        >
                                            {formatCellValue(column.key, text)}
                                        </div>
                                    )
                                };
                            }),
                            (() => {
                                const activeTabData = depoTabs.find(tab => tab.key === activeTab);
                                const hasActionButtons = activeTabData && activeTabData.actionButtons && activeTabData.actionButtons.length > 0;

                                if (hasActionButtons) {
                                    return {
                                        key: "actions",
                                        title: "İşlemler",
                                        width: 100,
                                        render: (text, record) => (
                                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start' }}>
                                                <ActionButtons
                                                    record={record}
                                                    actionButtons={activeTabData.actionButtons}
                                                    darkMode={darkMode}
                                                />
                                            </div>
                                        )
                                    };
                                }
                                return null;
                            })()
                        ].filter(Boolean)}
                    >

                    </Table>
                </div>
            </Card>
            )}

            {/* Form Modal */}
            {formModalOpen && (() => {
                return (
                    <Modal
                        title={modalTitle}
                        open={formModalOpen}
                        onCancel={closeFormModal}
                        footer={null}
                        destroyOnHidden={true}
                        width={800}
                    >
                        <div style={{ maxHeight: 600, overflowY: 'auto' }}>
                            <FormikForm
                                formId={currentFormId}
                                uniqueValue={currentUniqueValue}
                                uniqueField={formUniqueField}
                                tableName={formTableName}
                                extraData={formExtraData}
                                onSuccess={handleFormSuccess}
                            />
                        </div>
                    </Modal>
                );
            })()}
        </div>
    );
};

export default DepoPage;